import { AuthService } from './AuthService';

export interface EnergyPlusTestbedFrame {
  source: 'energyplus';
  date: string;
  time: string;
  timestamp: number;
  pointCount: number;
  snapshot: Record<string, number>;
}

export interface EnergyPlusControlState {
  controller: string;
  checkpoint: string;
  policy: string;
  actions: {
    ahu_supply_air_setpoint_c: number;
    chiller_on: boolean;
    chilled_water_setpoint_c: number;
    condenser_water_setpoint_c: number;
    buffer_tank_4_setpoint_c: number;
  };
  reward: number;
  comfort: number;
  ccr: number;
  system_on: boolean;
  equipment_running?: boolean;
  policy_active?: boolean;
  mode?: 'standby' | 'masac' | 'masac_tracking' | 'operator_manual' | 'advisory_accepted' | 'advisory_hold';
  operating_mode?: EnergyPlusOperatingMode;
  setpoint_source?: 'rl_policy_action' | 'standby_schedule' | 'control_disabled_system_off' | 'operator_manual' | 'advisory_previous_value';
  tracking_feedback_used?: EnergyPlusTrackingFeedback;
}

export type EnergyPlusOperatingMode = 'manual' | 'automatic' | 'advisory';

export interface EnergyPlusControlActions {
  ahu_supply_air_setpoint_c: number;
  chiller_on: boolean;
  chilled_water_setpoint_c: number;
  condenser_water_setpoint_c: number;
  buffer_tank_4_setpoint_c: number;
}

export interface EnergyPlusAdvisoryProposal {
  id: string;
  status: 'pending' | 'accepted' | 'rejected' | 'expired' | 'cancelled';
  step_index: number;
  simulation_timestamp: string;
  created_at: string;
  expires_at: string;
  actions: EnergyPlusControlActions;
  previous_actions: EnergyPlusControlActions;
  resolution?: string;
  resolved_at?: string;
  actor?: string;
  message?: string;
}

export interface EnergyPlusTrackingFeedback {
  applied: boolean;
  status: string;
  source?: string | null;
  target_epoch?: number;
  timestamp?: number | null;
  alignment_error_seconds?: number | null;
  zone_temp_c?: number | null;
  outdoor_temp_c?: number | null;
  occupancy_fraction?: number | null;
  confidence?: number | null;
  features?: string[];
  reason?: string | null;
  endpoint?: string;
}

export interface EnergyPlusTestbedStatus {
  running: boolean;
  stage: string;
  message: string;
  updated_at?: string;
  timestep_minutes?: number;
  step_delay_seconds?: number;
  step_index?: number;
  next_step_at?: string | null;
  operating_mode?: EnergyPlusOperatingMode;
  advisory_proposal?: EnergyPlusAdvisoryProposal | null;
  frame?: EnergyPlusTestbedFrame;
  control?: EnergyPlusControlState;
  energy?: {
    chiller_kw: number;
    cooling_tower_kw: number;
    ahu_fan_kw: number;
    bems_comparable_kw: number;
    scope: string;
  };
  temperatures?: Record<string, number>;
  artifacts?: Record<string, string>;
  tracking_relay?: {
    ok: boolean;
    endpoint?: string;
    timestamp?: number;
    skipped?: boolean;
    reason?: string | null;
    error?: string;
    disabled?: boolean;
  };
  tracking_feedback?: EnergyPlusTrackingFeedback;
  error?: string;
}

type Listener = (status: EnergyPlusTestbedStatus) => void;
const OFFLINE_FAILURE_THRESHOLD = 5;

const idleStatus = (): EnergyPlusTestbedStatus => ({
  running: false,
  stage: 'connecting',
  message: 'EnergyPlus virtual BEMS server is connecting.',
});

class _EnergyPlusTestbedService {
  private status: EnergyPlusTestbedStatus = idleStatus();
  private listeners = new Set<Listener>();
  private timer: ReturnType<typeof setInterval> | null = null;
  private pollInFlight = false;
  private consecutivePollFailures = 0;
  private lastInjectedFrameTimestamp: number | null = null;
  private injectingFrameTimestamp: number | null = null;

  start() {
    if (this.timer) return;
    void this.refresh();
    this.timer = setInterval(() => void this.refresh(), 1000);
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    this.start();
    listener(this.getStatus());
    return () => this.listeners.delete(listener);
  }

  getStatus(): EnergyPlusTestbedStatus {
    return {
      ...this.status,
      frame: this.status.frame
        ? { ...this.status.frame, snapshot: { ...this.status.frame.snapshot } }
        : undefined,
      control: this.status.control
        ? { ...this.status.control, actions: { ...this.status.control.actions } }
        : undefined,
    };
  }

  getSnapshot(): Record<string, number> {
    return { ...(this.status.frame?.snapshot ?? {}) };
  }

  getReading(pointId: string): { value: number; timestamp: number } | null {
    const value = this.status.frame?.snapshot?.[pointId];
    if (!Number.isFinite(value) || !this.status.frame) return null;
    return { value: Number(value), timestamp: this.status.frame.timestamp };
  }

  async startSession(stepDelaySeconds = 180) {
    this.lastInjectedFrameTimestamp = null;
    const response = await fetch('/api/eplus-testbed/session/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...AuthService.authHeaders() },
      body: JSON.stringify({
        step_delay_seconds: stepDelaySeconds,
        reset_logs: false,
        operator_requested: true,
      }),
    });
    if (!response.ok) throw new Error('EnergyPlus session could not be started.');
    this.status = await response.json();
    this.emit();
    return this.getStatus();
  }

  async stopSession() {
    const response = await fetch('/api/eplus-testbed/session/stop', {
      method: 'POST',
      headers: AuthService.authHeaders(),
    });
    if (!response.ok) throw new Error('EnergyPlus session could not be stopped.');
    this.status = await response.json();
    this.emit();
    return this.getStatus();
  }

  async restartSession(stepDelaySeconds = 180) {
    this.lastInjectedFrameTimestamp = null;
    const response = await fetch('/api/eplus-testbed/session/restart', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...AuthService.authHeaders() },
      body: JSON.stringify({
        step_delay_seconds: stepDelaySeconds,
        reset_logs: true,
        operator_requested: true,
      }),
    });
    if (!response.ok) throw new Error('EnergyPlus test session could not be restarted.');
    this.status = await response.json();
    this.emit();
    return this.getStatus();
  }

  async setSpeed(stepDelaySeconds: number) {
    const response = await fetch('/api/eplus-testbed/session/speed', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...AuthService.authHeaders() },
      body: JSON.stringify({ step_delay_seconds: stepDelaySeconds }),
    });
    if (!response.ok) throw new Error('EnergyPlus simulation pace could not be changed.');
    this.status = await response.json();
    this.emit();
    return this.getStatus();
  }

  async setOperatingMode(mode: EnergyPlusOperatingMode) {
    const response = await fetch('/api/eplus-testbed/session/control-mode', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...AuthService.authHeaders() },
      body: JSON.stringify({ mode }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'EnergyPlus operating mode could not be changed.');
    this.status = payload;
    this.emit();
    return this.getStatus();
  }

  async setManualSetpoints(actions: EnergyPlusControlActions) {
    const response = await fetch('/api/eplus-testbed/session/manual-setpoints', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...AuthService.authHeaders() },
      body: JSON.stringify(actions),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'Manual setpoints could not be queued.');
    this.status = payload;
    this.emit();
    return this.getStatus();
  }

  async decideAdvisory(proposalId: string, decision: 'accept' | 'reject') {
    const response = await fetch('/api/eplus-testbed/session/advisory-decision', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...AuthService.authHeaders() },
      body: JSON.stringify({ proposal_id: proposalId, decision, actor: AuthService.getUser()?.username || 'dashboard-operator' }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'The advisory decision could not be recorded.');
    return payload;
  }

  async waitForRunningFrame(timeoutMs = 180_000) {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      const response = await fetch('/api/eplus-testbed/status', {
        cache: 'no-store',
        headers: AuthService.authHeaders(),
      });
      if (!response.ok) throw new Error('EnergyPlus testbed status could not be read.');
      this.status = await response.json();
      this.emit();

      if (this.status.stage === 'failed' || this.status.stage === 'offline') {
        throw new Error(this.status.message || 'EnergyPlus testbed failed while starting.');
      }
      if (this.status.running && Number.isFinite(this.status.frame?.timestamp)) {
        await this.injectTrackingFrame();
        return this.getStatus();
      }
      await new Promise(resolve => window.setTimeout(resolve, 1_000));
    }
    throw new Error('EnergyPlus did not produce its first 20-minute frame in time.');
  }

  private async refresh() {
    if (this.pollInFlight) return;
    this.pollInFlight = true;
    try {
      const response = await fetch('/api/eplus-testbed/status', {
        cache: 'no-store',
        headers: AuthService.authHeaders(),
      });
      if (!response.ok) throw new Error('EnergyPlus testbed unavailable');
      this.status = await response.json();
      this.consecutivePollFailures = 0;
      this.emit();
      void this.injectTrackingFrame();
    } catch {
      this.consecutivePollFailures += 1;
      // Gateway or testbed restarts can drop a single one-second poll. Keep the
      // last verified frame visible during that short recovery window instead
      // of alarming the operator with a false "server connection error".
      if (this.consecutivePollFailures < OFFLINE_FAILURE_THRESHOLD) return;
      this.status = {
        ...this.status,
        running: false,
        stage: 'offline',
        message: 'The separate EnergyPlus server (8003) is unavailable.',
      };
      this.emit();
    } finally {
      this.pollInFlight = false;
    }
  }

  private emit() {
    const copy = this.getStatus();
    this.listeners.forEach(listener => listener(copy));
  }

  private async injectTrackingFrame() {
    const frame = this.status.frame;
    if (!frame || !Number.isFinite(frame.timestamp)) return;
    if (
      frame.timestamp === this.lastInjectedFrameTimestamp
      || frame.timestamp === this.injectingFrameTimestamp
    ) return;

    this.injectingFrameTimestamp = frame.timestamp;
    const snapshot = frame.snapshot;
    try {
      const response = await fetch('/api/twin/bms-inject', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          timestamp: frame.timestamp / 1000,
          outdoor_temp: snapshot['EPLUS.OUTDOOR.DRYBULB'],
          outdoor_rh: snapshot['EPLUS.OUTDOOR.RH'],
          zone_temps: {
            'DDC_M.D.D0116': snapshot['DDC_M.D.D0116'],
            'DDC_M.D.D0117': snapshot['DDC_M.D.D0117'],
            'DDC_M.D.D0118': snapshot['DDC_M.D.D0118'],
            'DDC_M.D.D0119': snapshot['DDC_M.D.D0119'],
          },
          facility_kw: snapshot['EPLUS.POWER.BEMS_COMPARABLE_KW'],
          cooling_kw: snapshot['EPLUS.POWER.BEMS_COMPARABLE_KW'],
          ahu_sa_temp: snapshot['EPLUS.AHU.SUPPLY_TEMP'],
          ahu_ra_temp: snapshot['EPLUS.AHU.RETURN_TEMP'],
          ahu_sa_temp_sp: snapshot['EPLUS.SETPOINT.AHU_SA_C'],
          zone_cooling_sp: 26,
          chw_supply: snapshot['EPLUS.CHW.SUPPLY_TEMP'],
          chw_return: snapshot['EPLUS.CHW.RETURN_TEMP'],
          chw_sp: snapshot['EPLUS.SETPOINT.CHW_C'],
          cw_sp: snapshot['EPLUS.SETPOINT.CW_C'],
          bt4_sp: snapshot['EPLUS.SETPOINT.BT4_C'],
          bt5_sp: 7,
          bt5_temp: snapshot['EPLUS.BT5.TANK_TEMP'],
          system_on: snapshot['EPLUS.STATUS.SYSTEM_ON'] >= 0.5,
          chiller_on: snapshot['EPLUS.STATUS.CHILLER_COMMAND_ON'] >= 0.5,
          setpoint_source: this.status.control?.policy_active
            ? 'rl_policy_action'
            : 'standby_schedule',
        }),
      });
      if (response.ok) this.lastInjectedFrameTimestamp = frame.timestamp;
    } catch {
      // The Tracking API may be restarting while EnergyPlus continues polling.
    } finally {
      if (this.injectingFrameTimestamp === frame.timestamp) {
        this.injectingFrameTimestamp = null;
      }
    }
  }
}

export const EnergyPlusTestbedService = new _EnergyPlusTestbedService();
